import { inject, Injectable } from '@angular/core';
import type { ResolveFn } from '@angular/router';
import type { Observable } from 'rxjs';
import { catchError, map, of } from 'rxjs';
import { HostnameService } from '../services/hostname.service';
import { TenantBrandingService } from './tenant-branding.service';
import type { TenantBranding } from './tenant-branding.types';

/**
 * Resolver function for tenant branding data
 * Ensures tenant branding is loaded before route activation
 */
export const tenantBrandingResolver: ResolveFn<TenantBranding | null> = (): Observable<TenantBranding | null> => {
  const tenantBrandingService = inject(TenantBrandingService);
  const hostnameService = inject(HostnameService);

  // Check if branding is already loaded
  if (tenantBrandingService.isBrandingLoaded()) {
    return of(tenantBrandingService.getCurrentBranding());
  }

  const fqdn = hostnameService.getFqdn();

  return tenantBrandingService.fetchTenantBrandingByFqdn(fqdn).pipe(
    map((branding) => {
      if (branding) {
        return branding;
      } else {
        // If no branding found, redirect to default or show error
        console.warn('No tenant branding found, using default');
        return null;
      }
    }),
    catchError((error) => {
      console.error('Failed to load tenant branding:', error);
      // Don't block navigation, just log the error
      return of(null);
    })
  );
};

/**
 * Service for tenant branding resolution
 * Provides additional methods for branding resolution
 */
@Injectable({
  providedIn: 'root',
})
export class TenantBrandingResolver {
  private readonly logger = console; // Replace with proper logger when available
  private readonly tenantBrandingService = inject(TenantBrandingService);
  private readonly hostnameService = inject(HostnameService);

  constructor() {
    this.logger.log('TenantBrandingResolver initialized');
  }

  /**
   * Resolve tenant branding for current domain
   */
  public resolveForCurrentDomain(): Observable<TenantBranding | null> {
    this.logger.log('Resolving tenant branding for current domain');

    const fqdn = this.hostnameService.getFqdn();

    return this.tenantBrandingService.fetchTenantBrandingByFqdn(fqdn).pipe(
      map((branding) => {
        if (branding) {
          this.logger.log('Tenant branding resolved successfully');
          return branding;
        } else {
          this.logger.warn('No tenant branding found for current domain');
          return null;
        }
      }),
      catchError((error) => {
        this.logger.error('Failed to resolve tenant branding:', error);
        return of(null);
      })
    );
  }

  /**
   * Check if tenant branding is available
   */
  public isBrandingAvailable(): boolean {
    return this.tenantBrandingService.isBrandingLoaded();
  }

  /**
   * Get current tenant branding
   */
  public getCurrentBranding(): TenantBranding | null {
    return this.tenantBrandingService.getCurrentBranding();
  }
}
